import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clinicalPhotoQueryKeys,
  clinicalPhotosApi,
  comparableMilestones,
  milestoneLabel,
  type MilestoneKind,
  type PhotoSeries,
} from "../../../shared/api/clinicalPhotos";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

const KIND_LABEL: Record<MilestoneKind, string> = {
  BEFORE: "Trước điều trị",
  PROGRESS: "Tiến triển",
  AFTER: "Sau điều trị",
};

const CONSENT_LABEL: Record<string, string> = {
  NOT_ASKED: "Chưa hỏi",
  GRANTED: "Đã đồng ý",
  WITHDRAWN: "Đã rút lại",
};

/**
 * One stored photo, rendered.
 *
 * The URL is fetched per photo and is short-lived, so it is not cached beyond its own lifetime —
 * a stale presigned URL renders as a broken image, which is worse than a second request.
 */
function PhotoThumb({
  photoId,
  alt,
  className,
}: {
  photoId: string;
  alt: string;
  className?: string;
}) {
  const url = useQuery({
    queryKey: clinicalPhotoQueryKeys.viewUrl(photoId),
    queryFn: () => clinicalPhotosApi.photoViewUrl(photoId),
    staleTime: 4 * 60_000, // under the 5-minute presign TTL
    gcTime: 4 * 60_000,
  });
  if (url.isError) {
    return (
      <div
        className={`flex aspect-square items-center justify-center rounded border bg-muted/40 p-1 text-center text-[10px] text-destructive ${className ?? ""}`}
      >
        Không tải được ảnh
      </div>
    );
  }
  if (!url.data) {
    return (
      <div
        className={`aspect-square animate-pulse rounded border bg-muted/40 ${className ?? ""}`}
      />
    );
  }
  return (
    <img
      src={url.data}
      alt={alt}
      loading="lazy"
      className={`aspect-square w-full rounded border object-cover ${className ?? ""}`}
    />
  );
}

/**
 * Clinical photo timeline for one patient (#18638).
 *
 * Two things this screen deliberately does:
 *
 * 1. Lets the clinician compare ANY two milestones, not a fixed before-vs-after. "Buổi 3 so với
 *    buổi 7" is the question a course of treatment actually raises.
 * 2. Keeps the marketing-permission control visually apart from the clinical images, because they
 *    are different decisions — a patient who refuses marketing still gets their treatment
 *    documented, and mixing the two invites a click that means more than it looks like.
 */
export function ClinicalPhotoPanel({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [openSeriesId, setOpenSeriesId] = useState<string | null>(null);
  const [leftId, setLeftId] = useState<string>("");
  const [rightId, setRightId] = useState<string>("");

  const [uploadingFor, setUploadingFor] = useState<string | null>(null);

  const q = useQuery({
    queryKey: clinicalPhotoQueryKeys.forPatient(patientId),
    queryFn: () => clinicalPhotosApi.listForPatient(patientId),
  });

  const refresh = () =>
    qc.invalidateQueries({
      queryKey: clinicalPhotoQueryKeys.forPatient(patientId),
    });

  /** A new dated milestone on the open series — "buổi 3", the thing photos hang off. */
  const addMilestone = useMutation({
    mutationFn: (v: { seriesId: string; kind: MilestoneKind }) =>
      clinicalPhotosApi.addMilestone(v.seriesId, { kind: v.kind }),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  /**
   * Upload straight to storage, then record the key.
   *
   * Several files at once because a milestone is usually several angles of the same sitting —
   * mặt trước / trái / phải — and making the clinician repeat the dialogue three times is how a
   * feature stops being used. Sequential, not parallel: a phone photo is large and a clinic
   * connection is not, and a burst of parallel PUTs is how uploads start timing out.
   */
  const uploadPhotos = useMutation({
    mutationFn: async (v: { milestoneId: string; files: File[] }) => {
      for (const f of v.files) {
        await clinicalPhotosApi.uploadPhoto(v.milestoneId, f);
      }
    },
    onMutate: (v) => setUploadingFor(v.milestoneId),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
    onSettled: () => setUploadingFor(null),
  });

  const series: PhotoSeries[] = q.data ?? [];
  const open = series.find((s) => s.id === openSeriesId) ?? series[0] ?? null;
  const milestones = useMemo(
    () => (open ? comparableMilestones(open) : []),
    [open],
  );

  const left = milestones.find((m) => m.id === leftId) ?? milestones[0] ?? null;
  const right =
    milestones.find((m) => m.id === rightId) ??
    milestones[milestones.length - 1] ??
    null;

  const createSeries = useMutation({
    mutationFn: (label: string) =>
      clinicalPhotosApi.createSeries(patientId, { label }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: clinicalPhotoQueryKeys.forPatient(patientId),
      });
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  // The wording shown at the counter. Fetched rather than hard-coded in the UI so the text the
  // patient was read and the text stored against their consent record are the same string.
  const consentText = useQuery({
    queryKey: ["clinical-photos", "consent-text"],
    queryFn: () => clinicalPhotosApi.marketingConsentText(),
    staleTime: 60 * 60 * 1000,
  });

  const setConsent = useMutation({
    mutationFn: (v: { seriesId: string; confirmed: boolean }) =>
      clinicalPhotosApi.setConsent(v.seriesId, { confirmed: v.confirmed }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: clinicalPhotoQueryKeys.forPatient(patientId),
      });
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Đang tải ảnh điều trị…</p>;
  }
  if (q.isError) {
    return <p className="text-sm text-destructive">Không tải được ảnh điều trị.</p>;
  }

  return (
    <div className="grid gap-4">
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {series.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setOpenSeriesId(s.id);
                setLeftId("");
                setRightId("");
              }}
              className={
                "rounded-md border px-3 py-1.5 text-sm " +
                (open?.id === s.id ? "bg-muted font-medium" : "bg-background")
              }
            >
              {s.label || "Hồ sơ ảnh"}
            </button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={createSeries.isPending}
          onClick={() => {
            const label = window.prompt("Tên hồ sơ ảnh (vd: Trị nám má)");
            if (label) createSeries.mutate(label);
          }}
        >
          + Hồ sơ ảnh mới
        </Button>
      </div>

      {!open && (
        <p className="text-sm text-muted-foreground">
          Chưa có hồ sơ ảnh nào. Tạo một hồ sơ để bắt đầu ghi nhận tiến triển theo thời gian.
        </p>
      )}

      {open && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                So sánh hai mốc bất kỳ
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Chọn hai thời điểm để đặt cạnh nhau — không cố định phải là mốc đầu và mốc cuối.
              </p>
            </CardHeader>
            <CardContent className="grid gap-3 pt-0">
              {milestones.length < 2 ? (
                <p className="text-sm text-muted-foreground">
                  Cần ít nhất hai mốc ảnh mới so sánh được. Hiện có {milestones.length}.
                </p>
              ) : (
                <>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <select
                      className="h-9 rounded-md border bg-background px-2 text-sm"
                      value={left?.id ?? ""}
                      onChange={(e) => setLeftId(e.target.value)}
                      aria-label="Mốc bên trái"
                    >
                      {milestones.map((m) => (
                        <option key={m.id} value={m.id}>
                          {milestoneLabel(m)}
                        </option>
                      ))}
                    </select>
                    <select
                      className="h-9 rounded-md border bg-background px-2 text-sm"
                      value={right?.id ?? ""}
                      onChange={(e) => setRightId(e.target.value)}
                      aria-label="Mốc bên phải"
                    >
                      {milestones.map((m) => (
                        <option key={m.id} value={m.id}>
                          {milestoneLabel(m)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[left, right].map((m, i) => (
                      <div key={i} className="grid gap-1">
                        <span className="text-xs font-medium">
                          {m ? milestoneLabel(m) : "—"}
                        </span>
                        <div className="grid grid-cols-2 gap-1">
                          {(m?.photos ?? []).map((p) => (
                            <PhotoThumb
                              key={p.id}
                              photoId={p.id}
                              alt={`${m ? milestoneLabel(m) : "Ảnh"}${p.viewAngle ? ` — ${p.viewAngle}` : ""}`}
                            />
                          ))}
                          {(m?.photos ?? []).length === 0 && (
                            <span className="text-xs text-muted-foreground">
                              Chưa có ảnh
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Dòng thời gian</CardTitle>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground">Thêm mốc:</span>
                {(["BEFORE", "PROGRESS", "AFTER"] as MilestoneKind[]).map((k) => (
                  <Button
                    key={k}
                    size="sm"
                    variant="outline"
                    disabled={!openSeriesId || addMilestone.isPending}
                    onClick={() =>
                      openSeriesId &&
                      addMilestone.mutate({ seriesId: openSeriesId, kind: k })
                    }
                  >
                    {KIND_LABEL[k]}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 pt-0">
              {milestones.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Chưa có mốc ảnh nào trong hồ sơ này.
                </p>
              )}
              {milestones.map((m) => (
                <div key={m.id} className="grid gap-2 border-b py-2 last:border-b-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="grid gap-0.5">
                      <span className="text-sm font-medium">
                        {KIND_LABEL[m.kind]}
                      </span>
                      <small className="text-xs text-muted-foreground">
                        {new Date(m.takenAt).toLocaleString("vi-VN")}
                        {m.encounterId ? " • gắn với lần khám" : ""}
                        {m.note ? ` • ${m.note}` : ""}
                      </small>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {m.photos.length} ảnh
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={uploadingFor === m.id}
                        onClick={() =>
                          document.getElementById(`upload-${m.id}`)?.click()
                        }
                      >
                        {uploadingFor === m.id ? "Đang tải…" : "Thêm ảnh"}
                      </Button>
                      <input
                        id={`upload-${m.id}`}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files ?? []);
                          // Reset first: picking the same file twice in a row fires no change
                          // event otherwise, and the clinician thinks the upload silently failed.
                          e.target.value = "";
                          if (files.length > 0) {
                            uploadPhotos.mutate({ milestoneId: m.id, files });
                          }
                        }}
                      />
                    </div>
                  </div>
                  {m.photos.length > 0 && (
                    <div className="grid grid-cols-4 gap-1 sm:grid-cols-6">
                      {m.photos.map((p) => (
                        <PhotoThumb
                          key={p.id}
                          photoId={p.id}
                          alt={`${KIND_LABEL[m.kind]}${p.viewAngle ? ` — ${p.viewAngle}` : ""}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Quyền sử dụng ảnh cho marketing
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Tách riêng với hồ sơ điều trị. Bệnh nhân từ chối vẫn được lưu ảnh phục vụ điều trị —
                mục này chỉ quyết định việc dùng ảnh ra bên ngoài (Nghị định 13).
              </p>
            </CardHeader>
            <CardContent className="grid gap-3 pt-0">
              {/* Read this to the patient before ticking. The checkbox is a staff attestation of
                  what they said, so the wording has to be on screen at the moment it is ticked. */}
              <blockquote className="rounded border-l-2 border-muted-foreground/40 bg-muted/30 p-3 text-xs leading-relaxed">
                {consentText.data ?? "Đang tải nội dung đồng ý…"}
              </blockquote>

              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={open.marketingConsentStatus === "GRANTED"}
                  disabled={setConsent.isPending || !consentText.data}
                  onCheckedChange={(checked: boolean | "indeterminate") => {
                    setError(null);
                    setConsent.mutate({
                      seriesId: open.id,
                      confirmed: checked === true,
                    });
                  }}
                  className="mt-0.5"
                />
                <span className="grid gap-0.5">
                  <span className="text-sm font-medium">
                    Bệnh nhân đã đồng ý sau khi được đọc nội dung trên
                  </span>
                  <small className="text-xs text-muted-foreground">
                    Ghi nhận tại quầy bởi nhân viên đang đăng nhập. Bỏ tích để ghi nhận bệnh nhân
                    rút lại đồng ý — lần đồng ý trước vẫn được lưu lại.
                  </small>
                </span>
              </label>

              <div className="text-xs text-muted-foreground">
                Trạng thái:{" "}
                <strong>
                  {CONSENT_LABEL[open.marketingConsentStatus] ??
                    open.marketingConsentStatus}
                </strong>
                {open.marketingConsentAt && (
                  <> • đồng ý {new Date(open.marketingConsentAt).toLocaleString("vi-VN")}</>
                )}
                {open.marketingWithdrawnAt && (
                  <> • rút lại {new Date(open.marketingWithdrawnAt).toLocaleString("vi-VN")}</>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
